/** In-memory GET dedupe + short TTL cache for the axios API client. */

export function createGetCache({ ttlMs = 15000 } = {}) {
  const cache = new Map();
  const inflight = new Map();

  const keyFor = (url, config = {}) => {
    const params = config.params ? JSON.stringify(config.params) : "";
    return `${url}?${params}`;
  };

  const shouldBypass = (config = {}) =>
    Boolean(config.skipCache || config.responseType === "blob");

  const wrapGet = (getFn, url, config = {}) => {
    if (shouldBypass(config)) {
      return getFn(url, config);
    }
    const key = keyFor(url, config);
    const cached = cache.get(key);
    if (cached && Date.now() - cached.at < ttlMs) {
      return Promise.resolve(cached.response);
    }
    const pending = inflight.get(key);
    if (pending) {
      return pending;
    }
    const request = Promise.resolve(getFn(url, config))
      .then((response) => {
        cache.set(key, { at: Date.now(), response });
        return response;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, request);
    return request;
  };

  const invalidate = (prefix) => {
    if (!prefix) {
      cache.clear();
      inflight.clear();
      return;
    }
    for (const key of [...cache.keys()]) {
      if (key.startsWith(prefix)) {
        cache.delete(key);
      }
    }
  };

  return { wrapGet, invalidate, keyFor };
}
